import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, ofActionDispatched, ofActionSuccessful, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { forkJoin } from 'rxjs';
import { debounceTime, filter, groupBy, map, mergeMap, takeUntil } from 'rxjs/operators';
import { GridInstanceWithFilterSettingsRemoteDto } from '../../../../../../generated/signalR/grid-instance-with-filter-settings-remote-dto';
import { BrickWallService, GridFilterSettingsRemoteDto } from '../../../../../../generated/swagger';
import {
   FilterItemsCleared,
   GridFilterSettingsChanged,
   GridFilterSettingsUpdatedByServer,
   PersistAllGridFilterSettings,
   PersistGridFilterSettings,
   StartToPersistAllGridFilterSettings,
   ToggleFilterPopup,
} from './grid-filter.actions';

export interface IFilterSettings {
   isFilterOpen: boolean;
   selectedValues: string[];
}

export interface IGridFilterSettings {
   filterSettings: { [filterNameKey: string]: IFilterSettings };
}

export interface IBrickFilterSetting {
   // all gridFilters for one single brick instance

   gridFilters: { [gridNameKey: string]: IGridFilterSettings };
}

export interface GridFilterStateModel {
   // key is BrickInstanceId
   brickFilterSettings: { [brickInstanceId: string]: IBrickFilterSetting };
}

@State<GridFilterStateModel>({
   name: 'gridFilters',
   defaults: {
      brickFilterSettings: {},
   },
})
@Injectable()
export class GridFilterState {
   constructor(private brickWallService: BrickWallService, private store: Store, private actions$: Actions) {
      this.actions$
         .pipe(
            ofActionSuccessful(StartToPersistAllGridFilterSettings),
            groupBy((x: StartToPersistAllGridFilterSettings) => x.brickInstanceId),
            mergeMap((group) =>
               group.pipe(
                  debounceTime(1500),
                  map((x) => this.store.dispatch(new PersistGridFilterSettings(x.brickInstanceId)))
               )
            )
         )
         .subscribe();
   }

   private static getDefault(filterId: string): IFilterSettings {
      return {
         isFilterOpen: false,
         selectedValues: [],
      };
   }

   private static getSpecificFilterOrDefaultInternal(state: GridFilterStateModel, brickInstanceId: string, gridId: string, filterId: string): IFilterSettings {
      const brickFilterSettings: IBrickFilterSetting = state.brickFilterSettings[brickInstanceId];
      if (!brickFilterSettings) {
         return this.getDefault(filterId);
      }
      const gridFilterSettings: IGridFilterSettings = brickFilterSettings.gridFilters[gridId];
      if (!gridFilterSettings) {
         return this.getDefault(filterId);
      }
      const filterSettings: IFilterSettings = gridFilterSettings.filterSettings[filterId];
      if (!filterSettings) {
         return this.getDefault(filterId);
      }
      return filterSettings;
   }

   @Selector([GridFilterState])
   public static getBrickFilterConfiguration(state: GridFilterStateModel): (brickInstanceId: string, gridId: string, filterId: string) => IFilterSettings {
      return (brickInstanceId: string, gridId: string, filterId: string) => {
         return GridFilterState.getSpecificFilterOrDefaultInternal(state, brickInstanceId, gridId, filterId);
      };
   }

   @Selector([GridFilterState.getBrickFilterConfiguration])
   public static getIsFilterOpen(filterFn) {
      return (brickInstanceId: string, gridId: string, filterId: string) => {
         const instance: IFilterSettings = filterFn(brickInstanceId, gridId, filterId);
         return instance.isFilterOpen;
      };
   }

   @Selector([GridFilterState.getBrickFilterConfiguration])
   public static selectedFilterValues(filterFn): (brickInstanceId: string, gridId: string, filterId: string) => string[] {
      return (brickInstanceId: string, gridId: string, filterId: string) => {
         const instance: IFilterSettings = filterFn(brickInstanceId, gridId, filterId);
         return instance.selectedValues;
      };
   }

   @Selector([GridFilterState.getBrickFilterConfiguration])
   public static getIsFilterSelected(getBrickFilterConfigurationFn) {
      return (brickInstanceId: string, gridId: string, filterId: string, filterValueKey: string): boolean => {
         const filterSetting: IFilterSettings = getBrickFilterConfigurationFn(brickInstanceId, gridId, filterId);
         return filterSetting.selectedValues.some((x) => x.toUpperCase() === filterValueKey);
      };
   }

   private augmentState(state: Readonly<GridFilterStateModel>, brickInstanceId: string, gridName: string) {
      if (_.isNil(state.brickFilterSettings[brickInstanceId])) {
         state.brickFilterSettings[brickInstanceId] = { gridFilters: {} };
      }
      if (_.isNil(state.brickFilterSettings[brickInstanceId].gridFilters[gridName])) {
         state.brickFilterSettings[brickInstanceId].gridFilters[gridName] = { filterSettings: {} };
      }
   }

   @Action(GridFilterSettingsUpdatedByServer)
   @ImmutableContext()
   public gridFilterSettingsUpdatedByServer(ctx: StateContext<GridFilterStateModel>, action: GridFilterSettingsUpdatedByServer) {
      ctx.setState((state) => {
         _.forEach(action.data.gridFilterSettings, (brickGrids: GridInstanceWithFilterSettingsRemoteDto, brickInstanceId) => {
            _(brickGrids.gridFilterSettings).forEach((gridFilterState: GridFilterSettingsRemoteDto, gridName) => {
               _(gridFilterState.filterConfigs).forEach((selectedValues: string[], gridFilterName) => {
                  if (_.isNil(selectedValues)) {
                     selectedValues = [];
                  }
                  const filterSetting = GridFilterState.getSpecificFilterOrDefaultInternal(state, brickInstanceId, gridName, gridFilterName);
                  console.log(`${brickInstanceId} => ${gridName} => ${gridFilterName} => ${selectedValues.length}`);
                  filterSetting.selectedValues = selectedValues;
                  this.augmentState(state, brickInstanceId, gridName);
                  state.brickFilterSettings[brickInstanceId].gridFilters[gridName].filterSettings[gridFilterName] = filterSetting;
               });
            });
         });
         return state;
      });
   }

   @Action(ToggleFilterPopup)
   @ImmutableContext()
   public toggleFilterPopup(ctx: StateContext<GridFilterStateModel>, action: ToggleFilterPopup) {
      ctx.setState((state) => {
         this.augmentState(state, action.brickInstanceId, action.gridId);
         const filterSetting = GridFilterState.getSpecificFilterOrDefaultInternal(state, action.brickInstanceId, action.gridId, action.filterId);
         filterSetting.isFilterOpen = !filterSetting.isFilterOpen;
         state.brickFilterSettings[action.brickInstanceId].gridFilters[action.gridId].filterSettings[action.filterId] = filterSetting;
         return state;
      });
   }

   @Action(GridFilterSettingsChanged)
   @ImmutableContext()
   public gridFilterSettingsChanged(ctx: StateContext<GridFilterStateModel>, action: GridFilterSettingsChanged) {
      ctx.setState((state) => {
         this.augmentState(state, action.brickInstanceId, action.gridId);
         if (action.isSelected) {
            state.brickFilterSettings[action.brickInstanceId].gridFilters[action.gridId].filterSettings[action.filterId].selectedValues.push(
               action.selection.filterKey
            );
         } else {
            _.remove(
               state.brickFilterSettings[action.brickInstanceId].gridFilters[action.gridId].filterSettings[action.filterId].selectedValues,
               (x) => x === action.selection.filterKey
            );
         }
         return state;
      });
      return this.store.dispatch(new StartToPersistAllGridFilterSettings(action.brickInstanceId));
   }

   @Action(FilterItemsCleared)
   @ImmutableContext()
   public filterItemsCleared(ctx: StateContext<GridFilterStateModel>, action: FilterItemsCleared) {
      const state = ctx.getState();
      if (_.isNil(state.brickFilterSettings[action.brickInstanceId]) || _.isNil(state.brickFilterSettings[action.brickInstanceId].gridFilters[action.gridId])) {
         return;
      }
      ctx.setState((x) => {
         x.brickFilterSettings[action.brickInstanceId].gridFilters[action.gridId].filterSettings = {};
         return x;
      });
      return this.store.dispatch(new StartToPersistAllGridFilterSettings(action.brickInstanceId));
   }

   @Action(PersistGridFilterSettings)
   public persistGridFilterSettings(ctx: StateContext<GridFilterStateModel>, action: PersistGridFilterSettings) {
      const state = ctx.getState();
      if (_.isNil(state.brickFilterSettings[action.brickInstanceId])) {
         return;
      }
      const gridFilterSettings: { [key: string]: GridFilterSettingsRemoteDto } = _.mapValues(
         state.brickFilterSettings[action.brickInstanceId].gridFilters,
         (gridFilterSettingsRemoteDto) => {
            return <GridFilterSettingsRemoteDto>{
               filterConfigs: _.mapValues(gridFilterSettingsRemoteDto.filterSettings, (filterSetting) => filterSetting.selectedValues),
            };
         }
      );
      return this.brickWallService
         .brickGridFilterSettings({
            brickInstanceId: action.brickInstanceId,
            gridFilterSettings: gridFilterSettings,
         })
         .pipe(
            takeUntil(
               this.actions$.pipe(
                  ofActionDispatched(StartToPersistAllGridFilterSettings),
                  filter((x: StartToPersistAllGridFilterSettings) => x.brickInstanceId === action.brickInstanceId)
               )
            )
         );
   }

   @Action(PersistAllGridFilterSettings)
   public persistAllGridFilterSettings(ctx: StateContext<GridFilterStateModel>, action: PersistAllGridFilterSettings) {
      const observables = action.brickInstanceIds.map((brickInstanceId) => ctx.dispatch(new PersistGridFilterSettings(brickInstanceId)));
      if (_.isEmpty(observables)) {
         return;
      }

      return forkJoin(observables);
   }
}
